import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicService, ScheduleContext } from '../services/publicService';
import SchedulingFlow, { SchedulingApi } from '../components/scheduling/SchedulingFlow';

/**
 * Public reschedule page for a single meeting (opened from the {meeting_url}
 * link). Renders the exact same UI as the patient self-schedule page in
 * "manage" mode — it just supplies a meeting-uuid API to <SchedulingFlow>.
 */
const UpdateMeetingPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();

  const api: SchedulingApi = useMemo(() => ({
    getContext: async (): Promise<ScheduleContext> => {
      const c = await publicService.getMeetingUpdateContext(uuid!);
      return {
        mode: c.reschedulable ? 'manage' : 'none',
        clinic: c.clinic,
        patient_name: c.patient_name ?? undefined,
        active_meeting: c.reschedulable
          ? {
              id: c.meeting.id,
              yoman_id: '',
              yoman_url: null,
              meeting_type_id: c.meeting.meeting_type_id,
              meeting_type_name: c.meeting.meeting_type_name,
              scheduled_at: c.meeting.scheduled_at,
              duration_minutes: c.meeting.duration_minutes,
            }
          : null,
        next_meeting: null,
        yoman_invite_url: null,
      };
    },
    // meeting-uuid endpoints identify the meeting by uuid, so the meetingType/meetingId
    // args from the shared flow are not needed here.
    getFirstAvailableMonth: () => publicService.getMeetingFirstAvailableMonth(uuid!),
    getAvailableSlots: (_meetingTypeId, from, to) => publicService.getMeetingAvailableSlots(uuid!, from, to),
    rescheduleMeeting: (_meetingId, data) => publicService.rescheduleMeetingByUuid(uuid!, data),
    cancelMeeting: (_meetingId, data) => publicService.cancelMeetingByUuid(uuid!, data),
    // no bookMeeting: this page manages one existing meeting only
  }), [uuid]);

  return <SchedulingFlow api={api} invalidLink={!uuid} blockedMessage="This meeting can no longer be updated." />;
};

export default UpdateMeetingPage;
